import { useState, useRef, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface TooltipProps {
  text: string;
  children?: ReactNode;
  position?: 'top' | 'bottom';
}

/**
 * Tooltip component with hover/focus trigger
 * Used in admin pages to provide context for form fields
 */
export function Tooltip({ text, children, position = 'top' }: TooltipProps) {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const show = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(true);
  };

  const hide = () => {
    timeoutRef.current = window.setTimeout(() => setIsVisible(false), 150);
  };

  const positionClasses = position === 'top'
    ? 'bottom-full left-1/2 -translate-x-1/2 mb-2'
    : 'top-full left-1/2 -translate-x-1/2 mt-2';

  const arrowClasses = position === 'top'
    ? 'top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-x-transparent border-b-transparent'
    : 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-x-transparent border-t-transparent';

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full"
        aria-label={t('tooltip.moreInfo', 'More information')}
      >
        {children || <HelpCircle className="w-4 h-4" />}
      </button>

      {isVisible && (
        <div
          className={`absolute z-50 ${positionClasses} px-3 py-2
                     text-xs text-white bg-gray-900 rounded-lg shadow-lg
                     max-w-xs whitespace-normal text-center`}
          role="tooltip"
        >
          {text}
          <div className={`absolute ${arrowClasses} border-4`} />
        </div>
      )}
    </div>
  );
}

export default Tooltip;
